// Commercial meters widget (DLMS ITRON via WG bridge, 10-min collector).
// Generic: pass `endpoint` (e.g. /scada/bolgrad/meters, /scada/prometheus/meters).
// Compact card for the right column of the station "Огляд" tab. Shows every point —
// those that read (green/amber by freshness) and those currently offline (grey · "офлайн").
// Refreshes every 60 s. Read-only (behind cabinet login).
import { useEffect, useState } from 'react';
import { API_BASE_URL, authFetch } from '@/services/api';
import { SectionCard } from '@/components/scada/kit';
import { scada } from '@/scada/tokens';

interface Meter {
  ktp: string;
  serial: number;
  aMinusWh: number | null;
  aPlusWh: number | null;
  pW: number | null;
  genTodayWh: number | null;
  clockMeter: string | null;
  status: string;
  ageSeconds: number | null;
}
interface Payload {
  meters: Meter[];
  summary: { count: number; online: number };
  ts: string;
}

const REFRESH_MS = 60000;
const COLS = 3;

function dotColor(m: Meter): string {
  if (m.status !== 'ok') return scada.textMuted;          // offline / no read → grey
  const age = m.ageSeconds;
  if (age == null) return scada.textMuted;
  if (age < 1200) return scada.state.ok;                  // < 20 min → fresh
  if (age < 3600) return scada.state.warning;             // < 1 h
  return scada.state.alarm;
}
function ago(age: number | null): string {
  if (age == null) return '—';
  if (age < 90) return `${age}с`;
  if (age < 3600) return `${Math.round(age / 60)}хв`;
  return `${(age / 3600).toFixed(1)}год`;
}

// накопичувальні регістри у МВт·год (3 знаки) та кВт·год (1 знак)
const mwh = (wh: number | null): string => (wh == null ? '—' : (wh / 1e6).toFixed(3));
const kwh = (wh: number | null): string => (wh == null ? '—' : (wh / 1000).toFixed(1));

export default function CommercialMetersWidget({ endpoint, cols = COLS }: { endpoint: string; cols?: number }) {
  const [data, setData] = useState<Payload | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const r = await authFetch(`${API_BASE_URL}${endpoint}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = (await r.json()) as Payload;
        if (alive) { setData(j); setErr(null); }
      } catch (e) {
        if (alive) setErr(e instanceof Error ? e.message : 'fetch error');
      }
    };
    void load();
    const id = setInterval(() => void load(), REFRESH_MS);
    return () => { alive = false; clearInterval(id); };
  }, [endpoint]);

  const sub = data
    ? `${data.summary.online}/${data.summary.count} онлайн · пряме DLMS-зчитування (ОЗД)`
    : 'пряме DLMS-зчитування (ОЗД)';

  return (
    <SectionCard fill title="Комерційний облік (DLMS)" subtitle={sub}>
      {err && !data && (
        <span style={{ color: scada.state.alarm, fontSize: 12 }}>Помилка: {err}</span>
      )}
      {!data && !err && (
        <span style={{ color: scada.textMuted, fontSize: 12 }}>Завантаження…</span>
      )}
      {data && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${cols}, 1fr)`,
            gridAutoRows: '1fr',
            gap: 8,
            flex: 1,
            width: '100%',
            minHeight: 0,
          }}
        >
          {data.meters.map((m, i) => {
            const isLast = i === data.meters.length - 1;
            const aloneOnRow = isLast && data.meters.length % cols === 1;
            const online = m.status === 'ok';
            const impWh = m.aPlusWh ?? 0;
            const expWh = m.aMinusWh ?? 0;
            const dominImport = impWh > expWh;
            const faceWh = dominImport ? m.aPlusWh : m.aMinusWh;
            const faceLabel = dominImport ? 'Імпорт' : 'Експорт';
            return (
              <div
                key={m.serial}
                style={{
                  background: scada.panel2,
                  border: `1px solid ${scada.border}`,
                  borderRadius: 8,
                  padding: '9px 11px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  gap: 3,
                  gridColumn: aloneOnRow ? '2 / 3' : undefined,
                  opacity: online ? 1 : 0.75,
                }}
              >
                <div className="flex items-center justify-between">
                  <span style={{ color: scada.text, fontSize: 11, fontWeight: 700 }}>{m.ktp}</span>
                  <span
                    title={online ? `оновлено ${ago(m.ageSeconds)} тому` : `офлайн · ${m.status}`}
                    style={{ width: 7, height: 7, borderRadius: 999, background: dotColor(m), display: 'inline-block' }}
                  />
                </div>
                <div style={{ marginTop: 4 }}>
                  <div style={{ fontSize: 8, color: scada.textDim, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    {faceLabel} · накопич.
                  </div>
                  <div
                    style={{
                      fontSize: 17,
                      fontWeight: 700,
                      lineHeight: 1.1,
                      color: online ? scada.text : scada.textMuted,
                    }}
                  >
                    {mwh(faceWh)}
                    <span style={{ fontSize: 8, color: scada.textDim, fontWeight: 600 }}> МВт·год</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, fontSize: 9, color: scada.textMuted, marginTop: 3 }}>
                  <span title="Активна енергія, експорт (генерація)">↑ {mwh(m.aMinusWh)}</span>
                  <span title="Активна енергія, імпорт (споживання)">↓ {mwh(m.aPlusWh)}</span>
                </div>
                <div style={{ color: scada.textMuted, fontSize: 9, marginTop: 2 }}>
                  {online
                    ? `P ${m.pW != null ? (m.pW / 1000).toFixed(1) : '—'} кВт · +${kwh(m.genTodayWh)} сьогодні · ${ago(m.ageSeconds)}`
                    : 'офлайн'}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SectionCard>
  );
}
